import type { FC } from "react";
import { useMemo, useState } from "react";

import { PixelPlaceholderLoader } from "@similarweb/ui-components";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import JobPositionDropdown from "app/pages/sales-intelligence/modules/onboarding/components/JobPositionDropdown/JobPositionDropdown";
import UserRoleModal from "app/pages/sales-intelligence/modules/onboarding/components/UserRoleModal/UserRoleModal";
import { JOB_POSITION_OTHER, JOB_POSITION_OTHER_UI } from "app/pages/sales-intelligence/modules/onboarding/constants";
import { FlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { StyledDropdownLabel } from "./styles";

interface IJobPositionContainerProps {
    data?: OnboardingInitData;
    isLoading?: boolean;
    onChange: (value: string, isCustomJobPosition: boolean) => void;
}

export const JobPositionContainer: FC<IJobPositionContainerProps> = ({ data, isLoading, onChange }) => {
    const [selectedValue, setSelectedValue] = useState<string | undefined>(data?.user?.jobTitle);
    const [isShowModal, setIsShowModal] = useState<boolean>(false);

    const mappedOptions = useMemo(
        () =>
            data?.options.userTitles.map((item: string) => {
                if (item === JOB_POSITION_OTHER) {
                    return JOB_POSITION_OTHER_UI;
                }

                return item;
            }),
        [data],
    );

    const handleValueSelect = (value: string, isCustomJobPosition: boolean) => {
        setSelectedValue(value);
        onChange?.(value, isCustomJobPosition);
    };

    const handleOnChange = (option: string) => {
        if (option === JOB_POSITION_OTHER_UI) {
            setIsShowModal(true);
            return;
        }
        handleValueSelect(option, false);
    };

    const handleOnClose = () => {
        setIsShowModal(false);
    };

    const handleOnSubmit = (value: string) => {
        const mappedValue = value.trim() || JOB_POSITION_OTHER;

        handleValueSelect(mappedValue, mappedValue !== JOB_POSITION_OTHER);
        setIsShowModal(false);
    };

    return (
        <FlexRow
            alignItems="center"
            gap="8px"
        >
            <I18n
                data-automation="job-position-prefix-text"
                element={StyledDropdownLabel}
                translatable={{ key: `si.onboarding.job-description.prefix.iam` }}
            />
            {isLoading ? (
                <PixelPlaceholderLoader
                    width={248}
                    height={32}
                />
            ) : null}
            {data && !isLoading ? (
                <JobPositionDropdown
                    selectedValue={selectedValue || data?.user?.jobTitle}
                    options={mappedOptions ?? []}
                    autoOpen={true}
                    onValueChange={handleOnChange}
                />
            ) : null}

            <I18n
                data-automation="job-position-postfix-text"
                element={StyledDropdownLabel}
                translatable={{ key: `si.onboarding.job-description.prefix.them-sells` }}
            />
            <UserRoleModal
                isOpened={isShowModal}
                onCancel={handleOnClose}
                onSubmit={handleOnSubmit}
            />
        </FlexRow>
    );
};
